import { useCallback, useState } from 'react';
import { FlatList, RefreshControl, Text, View } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { listPendingCustomTasks, resolveCustomTask } from '../api/admin';
import { ReviewTask } from '../types';
import { Button, Card, EmptyState, ErrorText, FormInput, Label, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { ApiError } from '../api/client';

export default function AdminCustomTasksScreen() {
  const { token } = useAuth();
  const [tasks, setTasks] = useState<ReviewTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setTasks(await listPendingCustomTasks(token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Không tải được danh sách nhiệm vụ, vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const onResolve = async (taskId: string, approve: boolean) => {
    if (!token) return;
    setBusyId(taskId);
    setError(null);
    try {
      await resolveCustomTask(token, taskId, approve, notes[taskId]);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Xử lý thất bại');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ScreenContainer>
      <ErrorText>{error}</ErrorText>
      <FlatList
        data={tasks}
        keyExtractor={(item) => item.id}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={load} />}
        ListEmptyComponent={!loading && !error ? <EmptyState text="Không có nhiệm vụ tự đề xuất nào đang chờ duyệt" /> : null}
        renderItem={({ item }) => (
          <Card>
            <Text style={{ fontSize: 16, fontFamily: fonts.bold, color: colors.text }}>{item.title}</Text>
            <Text style={{ color: colors.textMuted, marginTop: 2 }}>Shop: {item.shop?.name}</Text>
            <Text style={{ color: colors.text, marginTop: 8, fontSize: 13 }}>{item.description}</Text>
            <Text style={{ color: colors.textMuted, marginTop: 6, fontSize: 13 }}>{item.instructions}</Text>
            <Text style={{ color: colors.textMuted, marginTop: 6, fontSize: 12 }}>
              Thưởng {item.rewardCoins} xu x {item.totalSlots} lượt ·{' '}
              {item.requiresPurchase ? 'Yêu cầu mua hàng' : 'Không cần mua'}
            </Text>

            <View style={{ marginTop: 12 }}>
              <Label>Ghi chú (không bắt buộc)</Label>
              <FormInput
                value={notes[item.id] ?? ''}
                onChangeText={(v) => setNotes((n) => ({ ...n, [item.id]: v }))}
                placeholder="Lý do duyệt hoặc từ chối"
              />
              <Button
                title="Duyệt"
                onPress={() => onResolve(item.id, true)}
                loading={busyId === item.id}
              />
              <Button
                title="Từ chối"
                variant="danger"
                onPress={() => onResolve(item.id, false)}
                loading={busyId === item.id}
              />
            </View>
          </Card>
        )}
      />
    </ScreenContainer>
  );
}
